import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './AddPromotion.css';

const AddPromotion = () => {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: 'discount',
    discountType: 'percentage',
    discountValue: '',
    routes: 'all',
    startDate: '',
    endDate: '',
    status: 'active'
  });

  const [errors, setErrors] = useState({});

  // Placeholder routes (in real app, this would come from routes API)
  const routeOptions = [
    { value: 'all', label: 'All Routes' },
    { value: 'all-domestic', label: 'All Domestic Routes' },
    { value: 'all-international', label: 'All International Routes' },
    { value: 'lagos-london', label: 'Lagos → London' },
    { value: 'abuja-dubai', label: 'Abuja → Dubai' },
    { value: 'lagos-newyork', label: 'Lagos → New York' },
    { value: 'lagos-accra', label: 'Lagos → Accra' },
    { value: 'port-harcourt-lagos', label: 'Port Harcourt → Lagos' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Auto-uppercase for promotion code
    const processedValue = name === 'code' ? value.toUpperCase() : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Promotion name is required';
    }
    
    if (!formData.code.trim()) {
      newErrors.code = 'Promotion code is required';
    } else if (formData.code.length < 3) {
      newErrors.code = 'Promotion code must be at least 3 characters';
    }
    
    if (!formData.discountValue.trim()) {
      newErrors.discountValue = 'Discount value is required';
    } else {
      const numValue = parseFloat(formData.discountValue);
      if (isNaN(numValue) || numValue <= 0) {
        newErrors.discountValue = 'Discount value must be a positive number';
      }
      if (formData.discountType === 'percentage' && numValue > 100) {
        newErrors.discountValue = 'Percentage cannot exceed 100%';
      }
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) {
        newErrors.endDate = 'End date must be after start date';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Format discount value
      const discount = formData.discountType === 'percentage' 
        ? `${formData.discountValue}%`
        : `₦${parseFloat(formData.discountValue).toLocaleString()}`;
      
      const promotionData = {
        ...formData,
        discount
      };
      
      // Placeholder for save promotion
      console.log('Saving promotion:', promotionData);
      // Navigate back to promotions list
      navigate('/admin/promotions');
    }
  };

  const handleCancel = () => {
    navigate('/admin/promotions');
  };

  return (
    <AdminLayout pageTitle="Add Promotion">
      <div className="admin-add-promotion-page">
        {/* Page Title */}
        <div className="admin-add-promotion-header">
          <h1 className="admin-add-promotion-title">Add Promotion</h1>
          <nav className="admin-breadcrumb">
            <button 
              className="admin-breadcrumb-link"
              onClick={() => navigate('/admin/promotions')}
            >
              Promotions
            </button>
            <span className="admin-breadcrumb-separator">→</span>
            <span className="admin-breadcrumb-current">Add Promotion</span>
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="admin-promotion-form">
          {/* Promotion Form Card */}
          <div className="admin-form-section">
            <div className="admin-form-section-header">
              <h2 className="admin-form-section-title">Promotion Information</h2>
            </div>
            <div className="admin-form-section-content">
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Promotion Name <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.name ? 'error' : ''}`}
                    placeholder="e.g., Summer Sale"
                  />
                  {errors.name && (
                    <span className="admin-form-error">{errors.name}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Promotion Code <span className="admin-required">*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.code ? 'error' : ''}`}
                    placeholder="e.g., SUMMER2024"
                  />
                  {errors.code && (
                    <span className="admin-form-error">{errors.code}</span>
                  )}
                  <span className="admin-form-hint">Code will be auto-uppercased</span>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Type <span className="admin-required">*</span>
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="discount">Discount</option>
                    <option value="deal">Deal</option>
                    <option value="flash-sale">Flash Sale</option>
                  </select>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Discount Type <span className="admin-required">*</span>
                  </label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="amount">Fixed Amount (₦)</option>
                  </select>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Discount Value <span className="admin-required">*</span>
                  </label>
                  <input
                    type="number"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.discountValue ? 'error' : ''}`}
                    placeholder={formData.discountType === 'percentage' ? 'e.g., 15' : 'e.g., 50000'}
                    min="0"
                    max={formData.discountType === 'percentage' ? '100' : undefined}
                    step={formData.discountType === 'percentage' ? '0.1' : '1'}
                  />
                  {errors.discountValue && (
                    <span className="admin-form-error">{errors.discountValue}</span>
                  )}
                  <span className="admin-form-hint">
                    {formData.discountType === 'percentage' 
                      ? 'Enter percentage (0-100)' 
                      : 'Enter amount in Naira'}
                  </span>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Applicable Routes <span className="admin-required">*</span>
                  </label>
                  <select
                    name="routes"
                    value={formData.routes}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    {routeOptions.map(route => (
                      <option key={route.value} value={route.value}>
                        {route.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Start Date <span className="admin-required">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.startDate ? 'error' : ''}`}
                  />
                  {errors.startDate && (
                    <span className="admin-form-error">{errors.startDate}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    End Date <span className="admin-required">*</span>
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    className={`admin-form-input ${errors.endDate ? 'error' : ''}`}
                    min={formData.startDate || undefined}
                  />
                  {errors.endDate && (
                    <span className="admin-form-error">{errors.endDate}</span>
                  )}
                </div>

                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Status <span className="admin-required">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="admin-form-select"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-form-button admin-form-button-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="admin-form-button admin-form-button-primary"
            >
              Save Promotion
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AddPromotion;

